import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Avatar } from "antd";
import { FileOutlined, LoadingOutlined, RobotOutlined, UserOutlined, } from "@ant-design/icons";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useState } from "react";
import BotMessageCard from "@/components/Chat/BotMessageCard";
import { chatService } from "@/services/ChatService";
import { MessageAuthorType } from "@/types/chat";
import { replaceEmoji } from "@/utils/formatBotMessage";
import styles from "@/styles/MessageItem.module.less";
const SYSTEM_JOIN_RE = /joined the channel|added to the channel|removed from the channel|left the channel|updated the channel/i;
const AVATAR_COLORS = {
    [MessageAuthorType.DISPATCHER]: "#1890ff",
    [MessageAuthorType.TECHNICIAN]: "#52c41a",
    [MessageAuthorType.CLIENT]: "#fa8c16",
    [MessageAuthorType.BOT]: "#8c8c8c",
};
const AVATAR_ICONS = {
    [MessageAuthorType.DISPATCHER]: _jsx(UserOutlined, {}),
    [MessageAuthorType.TECHNICIAN]: _jsx(UserOutlined, {}),
    [MessageAuthorType.CLIENT]: _jsx(UserOutlined, {}),
    [MessageAuthorType.BOT]: _jsx(RobotOutlined, {}),
};
/** Renders file as thumbnail (images) or download link (others) */
const FileAttachment = ({ fileId }) => {
    const [thumbnailBlobUrl, setThumbnailBlobUrl] = useState(null);
    const [isBroken, setIsBroken] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    useEffect(() => {
        let revoked = false;
        chatService
            .fetchFileBlob(fileId, true)
            .then((blobUrl) => {
            if (!revoked)
                setThumbnailBlobUrl(blobUrl);
            else
                URL.revokeObjectURL(blobUrl);
        })
            .catch(() => {
            if (!revoked)
                setIsBroken(true);
        });
        return () => {
            revoked = true;
            if (thumbnailBlobUrl)
                URL.revokeObjectURL(thumbnailBlobUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileId]);
    const handleDownload = useCallback(async (e) => {
        e.preventDefault();
        if (isDownloading)
            return;
        setIsDownloading(true);
        try {
            const blobUrl = await chatService.fetchFileBlob(fileId);
            window.open(blobUrl, "_blank");
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        }
        catch {
            console.error("Failed to download attachment");
        }
        finally {
            setIsDownloading(false);
        }
    }, [fileId, isDownloading]);
    if (!isBroken && thumbnailBlobUrl) {
        return (_jsx("a", { href: "#", onClick: handleDownload, children: _jsx("img", { src: thumbnailBlobUrl, alt: "\u0412\u043B\u043E\u0436\u0435\u043D\u0438\u0435", className: styles.thumbnail }) }));
    }
    if (!isBroken && !thumbnailBlobUrl) {
        return (_jsxs("span", { className: styles.fileLink, children: [_jsx(LoadingOutlined, {}), _jsx("span", { children: "\u0412\u043B\u043E\u0436\u0435\u043D\u0438\u0435" })] }));
    }
    return (_jsxs("a", { href: "#", onClick: handleDownload, className: styles.fileLink, children: [isDownloading ? _jsx(LoadingOutlined, {}) : _jsx(FileOutlined, {}), _jsx("span", { children: "\u0412\u043B\u043E\u0436\u0435\u043D\u0438\u0435" })] }));
};
function formatTime(timestamp) {
    return new Date(timestamp).toLocaleTimeString("ru-RU", {
        hour: "2-digit",
        minute: "2-digit",
    });
}
const MessageItem = observer(({ message, isOwn }) => {
    const { authorType } = message;
    const isBot = authorType === MessageAuthorType.BOT;
    const rowClass = [
        styles.messageRow,
        isOwn ? styles["messageRow--own"] : "",
        isBot ? styles["messageRow--bot"] : "",
    ]
        .filter(Boolean)
        .join(" ");
    const bubbleClass = [
        styles.bubble,
        styles[`bubble--${authorType}`],
    ].join(" ");
    const nameClass = [
        styles.authorName,
        styles[`authorName--${authorType}`],
    ].join(" ");
    if (isBot) {
        // System join/leave messages — render as simple text, not rich card
        if (SYSTEM_JOIN_RE.test(message.message)) {
            return (_jsx("div", { className: rowClass, children: _jsx("div", { className: styles.systemText, children: replaceEmoji(message.message) }) }));
        }
        return (_jsx("div", { className: rowClass, children: _jsx(BotMessageCard, { text: message.message, timestamp: message.createAt }) }));
    }
    return (_jsxs("div", { className: rowClass, children: [_jsx("div", { className: styles.avatarCol, children: _jsx(Avatar, { size: 28, icon: AVATAR_ICONS[authorType], style: { backgroundColor: AVATAR_COLORS[authorType] } }) }), _jsxs("div", { className: bubbleClass, children: [!isOwn && _jsx("div", { className: nameClass, children: message.authorName }), _jsx("div", { className: styles.messageText, children: message.message }), message.fileIds.length > 0 && (_jsx("div", { className: styles.attachments, children: message.fileIds.map((fileId) => (_jsx(FileAttachment, { fileId: fileId }, fileId))) })), _jsx("div", { className: styles.messageTime, children: formatTime(message.createAt) })] })] }));
});
MessageItem.displayName = "MessageItem";
export default MessageItem;
